// @flow

import React, { useState, useEffect, useCallback } from 'react';
import { CSSTransition } from 'react-transition-group';
import Markdown from 'markdown-to-jsx';

import { ButtonIcon } from 'texkit/dist/components/Buttons';

import { Trans } from '../common';
import { history } from '../../store';

import './MessageBanner.scss';

type Message = {|
  messageId: string,
  textEnglish: string,
  textSpanish: string,
  startTime: string,
  endTime: string,
  priority: string,
  status: string,
  author: string,
  valid: boolean,
  expiresIn?: number,
  dismissOnRouteChange?: boolean,
|};

type MessageBannerProps = {|
  language: string,
  message: Message,
  dismissable: boolean,
  handleBannerClose?: Function,
  handleDismiss?: Function,
|};

type MessageBannersProps = {|
  messages: Array<Message>,
  language: string,
  dismissable?: boolean,
  handleClose?: Function,
|};

export function MessageBanner({
  language,
  message,
  dismissable,
  handleBannerClose,
  handleDismiss,
}: MessageBannerProps) {
  const text = language === 'en' ? message.textEnglish : message.textSpanish;
  const { messageId } = message;
  const [isDismissing, setIsDismissing] = useState(false);
  let jsonContent = {};

  function isJson(str) {
    try {
      jsonContent = JSON.parse(str);
    } catch {
      return false;
    }
    return true;
  }

  const jsonType = isJson(message.textEnglish);

  const dismissMessage = useCallback(() => {
    setIsDismissing(true);
    if (handleBannerClose) {
      handleBannerClose(messageId);
    }
  }, [handleBannerClose, messageId]);

  useEffect(() => {
    if (!isDismissing && message.expiresIn && handleBannerClose) {
      const timeoutId = window.setTimeout(() => {
        dismissMessage();
      }, message.expiresIn);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }
  }, [dismissMessage, isDismissing, message.expiresIn, handleBannerClose]);

  useEffect(() => {
    if (
      !isDismissing &&
      message.dismissOnRouteChange &&
      (handleDismiss || handleBannerClose)
    ) {
      const unlisten = history.listen(() => {
        if (handleDismiss) {
          setIsDismissing(true);
          handleDismiss(messageId);
        } else if (handleBannerClose) {
          dismissMessage();
        }
      });

      return () => {
        unlisten();
      };
    }
  }, [
    dismissMessage,
    handleBannerClose,
    handleDismiss,
    isDismissing,
    message.dismissOnRouteChange,
    messageId,
  ]);

  return (
    <li className={`message-banner message-banner-${message.priority}`}>
      <div>
        <div>
          {jsonType && (
            <Trans
              file={jsonContent.type}
              id={jsonContent.id}
              args={[jsonContent.args]}
            />
          )}
          {!jsonType && <Markdown>{text}</Markdown>}
        </div>
        {dismissable && handleBannerClose && (
          <ButtonIcon
            id={`meesage-banner-${message.messageId}`}
            icon="x"
            aria-label="Close message"
            onClick={dismissMessage}
          />
        )}
      </div>
    </li>
  );
}

export default function MessageBanners({
  messages,
  language,
  dismissable,
  handleClose,
}: MessageBannersProps) {
  const [dismissed, setDismissed] = useState([]);

  const onClose = useCallback(messageId => {
    setDismissed(prevDismissed => [...prevDismissed, messageId]);
  }, []);

  function handleOnExited(messageId) {
    if (dismissable && handleClose) {
      return function() {
        handleClose(messageId);
      };
    }
  }

  return (
    <ul className="message-banners">
      {messages.map(message => (
        <CSSTransition
          in={!dismissed.includes(message.messageId)}
          timeout={275}
          classNames="message-banner"
          onExited={handleOnExited(message.messageId)}
          unmountOnExit
          key={`message${message.messageId}`}
        >
          <MessageBanner
            message={message}
            language={language}
            dismissable={
              message.dismissable ? message.dismissable : dismissable
            }
            handleBannerClose={onClose}
            handleDismiss={handleClose}
          />
        </CSSTransition>
      ))}
    </ul>
  );
}
